import { MONEY_UNIT } from '@web-platform/shared-universal-constants/src/money';
import {
  formatMoney,
  mapMoney,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { hasUndefinedValues, roundUp } from './utils';

import type {
  SkyscannerCommonV1Money,
  SkyscannerPriceinsightsV2PriceInsights,
} from '@skyscanner-internal/price-insights-web-http-client';
import type { Money } from '@web-platform/shared-universal-constants/src/money';
import type { PriceBandsState } from '@web-platform/shared-universal-types/src/redux/state/priceBands/priceBands';

type PBMoney = SkyscannerCommonV1Money.Money;
type PriceBandsResponse = SkyscannerPriceinsightsV2PriceInsights.SearchPriceBandsResponse;

export const INITIAL_STATE = {
  pOutlierLow: undefined,
  pLow: undefined,
  pHigh: undefined,
  pOutlierHigh: undefined,
  percentileValueLow: undefined,
  percentileValueHigh: undefined,
  isLoading: true,
  isError: false,
};

const errorResponse = (response: PriceBandsResponse) => {
  const band = response?.priceBands?.[0];
  const degradationReasons = response?.degradationReasons ?? [];
  if (degradationReasons.length > 0 || (band && hasUndefinedValues(band))) {
    logWarn({
      component: 'PriceBands',
      message: `Degradation Reasons: ${JSON.stringify(degradationReasons)}, Price Bands: ${JSON.stringify(band)}`,
    });
    return true;
  }
  return false;
};

const mapAndRoundUpMoney = (priceBandsMoney: PBMoney | undefined): Money | undefined => {
  if (!priceBandsMoney) {
    return undefined;
  }

  const money = mapMoney(priceBandsMoney);

  if (!money) {
    return undefined;
  }

  return {
    amount: roundUp(formatMoney(money, false)).toString(),
    currency_code: money.currency_code,
    unit: MONEY_UNIT.UNIT_WHOLE,
  };
};

export const mapPriceBands = (response: PriceBandsResponse): PriceBandsState => {
  const band = response?.priceBands?.[0];
  const isError = errorResponse(response);

  if (isError || !band) {
    return { ...INITIAL_STATE, isLoading: false, isError };
  }

  return {
    pOutlierLow: mapAndRoundUpMoney(band.pOutlierLow),
    pLow: mapAndRoundUpMoney(band.pLow),
    pHigh: mapAndRoundUpMoney(band.pHigh),
    pOutlierHigh: mapAndRoundUpMoney(band.pOutlierHigh),
    percentileValueLow: band.percentileValueLow,
    percentileValueHigh: band.percentileValueHigh,
    isLoading: false,
    isError: false,
  };
};
